import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { money, statusLabel, bucketClass, Pill, shortDate } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, ChevronRight } from "lucide-react";

const BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"];

export default function Loans() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("all");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (bucket !== "all") params.bucket = bucket;
      if (q) params.q = q;
      const r = await api.get("/loans", { params });
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket, status]);

  const totals = items.reduce(
    (acc, l) => ({
      outstanding: acc.outstanding + (l.outstanding || 0),
      arrears: acc.arrears + (l.arrears || 0),
    }),
    { outstanding: 0, arrears: 0 }
  );

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Loan book
            </h1>
            <p className="text-sm text-slate-500">
              {items.length} loan{items.length === 1 ? "" : "s"} · {money(totals.outstanding)} outstanding
              {totals.arrears > 0 && <> · <span className="text-red-700 font-medium">{money(totals.arrears)} in arrears</span></>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <form
              onSubmit={(e) => { e.preventDefault(); load(); }}
              className="relative"
            >
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9 w-56"
                placeholder="Search by borrower email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="loan-search"
              />
            </form>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="loan-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="written_off">Written off</SelectItem>
              </SelectContent>
            </Select>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="w-40" data-testid="loan-bucket-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All buckets</SelectItem>
                {BUCKETS.map((b) => <SelectItem key={b} value={b}>{b === "current" ? "Current" : b + " days"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-8 text-center text-slate-500">Loading…</CardContent></Card>
        ) : items.length === 0 ? (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-10 text-center">
            <p className="font-medium">No loans match these filters</p>
            <p className="text-sm text-slate-500 mt-1">
              A loan appears here once its application is approved and disbursed.
            </p>
          </CardContent></Card>
        ) : (
          <>
            {/* desktop */}
            <Card className="rounded-xl border-slate-200 hidden lg:block"><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Borrower</th>
                      <th className="text-left px-4 py-2">Product</th>
                      <th className="text-right px-4 py-2">Outstanding</th>
                      <th className="text-right px-4 py-2">Arrears</th>
                      <th className="text-center px-4 py-2">DPD</th>
                      <th className="text-left px-4 py-2">Bucket</th>
                      <th className="text-left px-4 py-2">Next due</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => (
                      <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`loan-row-${l.application_id}`}>
                        <td className="px-4 py-2">{l.borrower_email}</td>
                        <td className="px-4 py-2 text-slate-600">{l.product_name}</td>
                        <td className="px-4 py-2 text-right font-medium tabular-nums">{money(l.outstanding)}</td>
                        <td className={"px-4 py-2 text-right tabular-nums " + (l.arrears > 0 ? "text-red-700 font-medium" : "text-slate-400")}>
                          {l.arrears > 0 ? money(l.arrears) : "—"}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">{l.days_past_due > 0 ? l.days_past_due : "—"}</td>
                        <td className="px-4 py-2"><Pill className={bucketClass(l.bucket)}>{statusLabel(l.bucket) === l.bucket ? l.bucket : statusLabel(l.bucket)}</Pill></td>
                        <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{shortDate(l.next_due_date)}</td>
                        <td className="px-4 py-2 text-right">
                          <Link to={`/admin/loans/${l.application_id}`}>
                            <Button variant="outline" size="sm" className="h-8 px-2 text-xs">
                              Open <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>

            {/* mobile */}
            <div className="space-y-3 lg:hidden">
              {items.map((l) => (
                <Link key={l.id} to={`/admin/loans/${l.application_id}`} className="block">
                  <Card className="rounded-xl border-slate-200"><CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{l.borrower_email}</div>
                        <div className="text-sm text-slate-500">{l.product_name}</div>
                      </div>
                      <Pill className={bucketClass(l.bucket)}>{l.bucket}</Pill>
                    </div>
                    <div className="flex justify-between text-sm pt-1 border-t border-slate-100">
                      <span className="text-slate-500">Outstanding</span>
                      <span className="font-medium tabular-nums">{money(l.outstanding)}</span>
                    </div>
                    {l.arrears > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Arrears · {l.days_past_due}d</span>
                        <span className="font-medium text-red-700 tabular-nums">{money(l.arrears)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Next due</span>
                      <span>{shortDate(l.next_due_date)}</span>
                    </div>
                  </CardContent></Card>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
